import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { duration, easing, reducedMotion } from '../styles/motion';
// [ONE GLASS RECIPE 2026-09-01, user] One tooltip surface for the whole app — see TipGlass.
import {
  GLASS_BLUR, GLASS_RADIUS, glassBackground, glassBorder, glassBorderColor, glassInk, glassPointer, glassShadow,
} from './TipGlass';
import { pageRect } from '../utils/appZoom'; // [APP-FIT 2026-10-26] placed in page px

type Placement = 'right' | 'bottom';

/* Slide-in from the trigger. Only transform/opacity (compositor) per motion rules. */
const popRight = keyframes`
  from { opacity: 0; transform: translateY(-50%) translateX(-4px); }
  to   { opacity: 1; transform: translateY(-50%) translateX(0); }
`;
const popBottom = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

/**
 * Glass capsule — the SAME surface the in-chart tooltips and the `title` capsule use, from the
 * shared recipe in TipGlass. Rendered into a body portal (fixed-positioned) so it escapes any
 * ancestor's overflow / backdrop-filter clip.
 *
 * [ONE GLASS RECIPE 2026-09-01, user] "make the tooltip same all over the application." This was
 * its own thing until now: a green-tinted always-dark capsule (rgba(28,38,33,0.82)) at blur 20px
 * and radius 9, with hardcoded white text. Two consequences of the change are worth knowing —
 * it is now THEME-AWARE (light glass with dark ink in light mode, where it used to stay dark), and
 * its ink comes from glassInk rather than a flat #fff.
 */
const Box = styled.div<{ $placement: Placement }>`
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  white-space: nowrap;
  transform: ${(p) => (p.$placement === 'right' ? 'translateY(-50%)' : 'translateX(-50%)')};

  padding: 7px 11px;
  border-radius: ${GLASS_RADIUS}px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${(p) => glassInk(p.theme.colorScheme === 'dark')};

  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};

  animation: ${(p) => (p.$placement === 'right' ? popRight : popBottom)} ${duration.fast} ${easing.entrance};

  /* Little diamond pointing back at the trigger. */
  &::before {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    background: ${(p) => glassPointer(p.theme.colorScheme === 'dark')};
    ${(p) => {
      const edge = `1px solid ${glassBorderColor(p.theme.colorScheme === 'dark')}`;
      return p.$placement === 'right'
        ? `
      top: 50%;
      left: -4px;
      transform: translateY(-50%) rotate(45deg);
      border-left: ${edge};
      border-bottom: ${edge};
    `
        : `
      left: 50%;
      top: -4px;
      transform: translateX(-50%) rotate(45deg);
      border-left: ${edge};
      border-top: ${edge};
    `;
    }}
  }

  ${reducedMotion} {
    animation: none;
  }
`;

interface TooltipProps {
  /** Text shown in the capsule. */
  label: string;
  /** Where the capsule sits relative to the trigger. */
  placement?: Placement;
  /** Set false to disable (e.g. only show when a rail is collapsed). */
  enabled?: boolean;
  /** Gap in px between trigger edge and capsule. */
  gap?: number;
  /** A single element trigger — handlers are attached to it, no extra DOM. */
  children: React.ReactElement<any>;
}

/**
 * Glass hover tooltip. Wraps a single trigger element and renders the capsule
 * into a body portal, so it is never clipped by the trigger's container.
 */
// [MOBILE 2026-07-07] True only on devices with a real hovering pointer (mouse/
// trackpad). On touch (hover: none) tapping a trigger fires an emulated
// mouseenter + focus that SHOW the tip, but no mouseleave ever fires, so it
// sticks forever (seen on the header's theme + trash buttons on phones). Gating
// show() on this means touch devices simply never open a hover tooltip — the
// correct behaviour, since touch has no hover.
const canHover = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover)').matches;

const Tooltip: React.FC<TooltipProps> = ({ label, placement = 'bottom', enabled = true, gap = 12, children }) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = (e: React.SyntheticEvent) => {
    if (!enabled || !canHover()) return;
    const r = pageRect(e.currentTarget as HTMLElement); // [APP-FIT 2026-10-26] page px, like the top / left it sets
    if (placement === 'right') {
      setPos({ top: r.top + r.height / 2, left: r.right + gap });
    } else {
      setPos({ top: r.bottom + gap, left: r.left + r.width / 2 });
    }
  };
  const hide = () => setPos(null);

  const child = React.cloneElement(children, {
    // [NESTED-TIP 2026-10-13, user] One tooltip at a time: GlobalTooltip shows no `title` capsule here.
    'data-own-tip': enabled ? '' : undefined,
    onMouseEnter: (e: any) => { children.props.onMouseEnter?.(e); show(e); },
    onMouseLeave: (e: any) => { children.props.onMouseLeave?.(e); hide(); },
    onFocus: (e: any) => { children.props.onFocus?.(e); show(e); },
    onBlur: (e: any) => { children.props.onBlur?.(e); hide(); },
  });

  return (
    <>
      {child}
      {enabled && pos && createPortal(
        <Box $placement={placement} style={{ top: pos.top, left: pos.left }}>{label}</Box>,
        document.body,
      )}
    </>
  );
};

export default Tooltip;
